import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { CalendarPopover } from '@/components/ui/CalendarPopover';
import { PickerPopoverShell } from '@/components/ui/PickerPopoverShell';
import { TimePickerPanel } from '@/components/ui/TimePickerPanel';
import type { SelectOption } from '@/components/ui/Select';

export type DateTimePickerPanelProps = {
  dateValue: string;
  viewMonth: Date;
  onViewMonthChange: (month: Date) => void;
  onDateSelect: (date: Date) => void;
  minDate?: string;
  maxDate?: string;
  hour: string;
  minute: string;
  onHourChange: (hour: string) => void;
  onMinuteChange: (minute: string) => void;
  hourOptions?: SelectOption[];
  minuteOptions?: SelectOption[];
  onApply: () => void;
  onCancel?: () => void;
  applyDisabled?: boolean;
  className?: string;
};

export function DateTimePickerPanel({
  dateValue,
  viewMonth,
  onViewMonthChange,
  onDateSelect,
  minDate,
  maxDate,
  hour,
  minute,
  onHourChange,
  onMinuteChange,
  hourOptions,
  minuteOptions,
  onApply,
  onCancel,
  applyDisabled,
  className,
}: DateTimePickerPanelProps) {
  const { t } = useTranslation('common');

  return (
    <PickerPopoverShell
      className={cn('datetime-picker-panel', className)}
      footer={
        <>
          {onCancel && (
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              {t('actions.cancel', { defaultValue: 'Cancel' })}
            </Button>
          )}
          <Button type="button" size="sm" disabled={applyDisabled} onClick={onApply}>
            {t('actions.apply', { defaultValue: 'Apply' })}
          </Button>
        </>
      }
    >
      <CalendarPopover
        embedded
        value={dateValue}
        viewMonth={viewMonth}
        onViewMonthChange={onViewMonthChange}
        onSelect={onDateSelect}
        min={minDate}
        max={maxDate}
        className="datetime-picker-calendar"
      />
      <div className="picker-popover-divider" aria-hidden />
      <TimePickerPanel
        embedded
        hour={hour}
        minute={minute}
        onHourChange={onHourChange}
        onMinuteChange={onMinuteChange}
        hourOptions={hourOptions}
        minuteOptions={minuteOptions}
        showApply={false}
        className="datetime-picker-time"
      />
    </PickerPopoverShell>
  );
}
